import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { v4 as uuidv4 } from 'uuid';
import { logging } from 'w3-user-ui-component';
import uilibPackageJson from 'w3-user-ui-component/package.json';

import packageJson from '../package.json';

// FOR TESTING -> can be removed when connected to backend.
// import DummyBasicData from './assets/data/DummyProfileData';
import ShrugIllustration from './assets/illustrations/ShrugIllustartion';
import Achievements from './components/Achievements';
import Activity from './components/Activity';
import Banner from './components/Banner';
import Certificates from './components/Certificates';
import Cohort from './components/Cohort';
import CompetitionPerformance from './components/CompetitionPerformance/CompetitionPerformance';
import ContactModal from './components/ContactModal';
import Discord from './components/Discord';
import Interests from './components/Interests';
import Notifications from './components/Notifications';
import { NotificationDismissType, NotificationType } from './components/Notifications/utils/TypesInterfaces';
import Skills from './components/Skills';
import Spaces from './components/Spaces';
import User from './components/User';
import LoadingBox from './components/userlib/LoadingBox';
import { NotificationStateContext } from './context/NotificationContext';
import { ProfileStateContext } from './context/ProfileDataContext';
import useFooterPosition from './hooks/UseFooterPosition';
import {
  ComponentTypes,
  IActivity,
  ICertificates,
  IDefault as IComponentDefault,
  IInterests,
  ISocialMedia,
  IUserProfileConfiguration,
  SocialMediaTypes,
} from './utils/interfaces/DbInterfaces';
import { getKaiApiBaseUrl, getPathfinderApiUrl, getPathfinderUrl } from './utils/pathsUtil';

import './App.scss';

/**
 * Heading in notification when something has gone wrong
 */
const ERROR_HEADING = 'Something went wrong';

// TESTING only, can be removed in final
// const TestingFailLoadin = false;

const PublicProfile = () => {
  const { userId } = useParams();
  const { profileData, setProfileData, setAvatarAssets, setUnlockRules, setAvatarAssetsLoaded } = useContext(ProfileStateContext);
  const { notifications, dismissNotification, addNNotification } = useContext(NotificationStateContext);

  const [isLoading, setIsLoading] = useState(false);
  const [userIsPublished, setUserIsPublished] = useState(false);
  const footerRef = React.createRef<HTMLDivElement>();
  const [initiatedPage, setInitiatedPage] = useState(false);
  const [initiateFotter, setInitiateFotter] = useState(false);
  const [showContactInfoModal, setShowContactInfoModal] = useState(false);
  const footerTimeoutId = useRef<NodeJS.Timeout>();
  const useIsFooterSetToFixed = useFooterPosition(footerRef, [initiateFotter]);
  const [responseText, setResponseText] = useState('');
  const mapSocialMedia = useCallback((data: any, type: SocialMediaTypes, field: keyof typeof data.contacts): ISocialMedia | null => {
    const contact = data?.contacts?.[field];

    if (contact) {
      return {
        type,
        userName: contact.value,
        visible: contact.visible,
      };
    }

    return null;
  }, []);

  // hook for initiating first time positioning of bottom banner
  useEffect(() => {
    if (initiatedPage && footerRef.current && !footerTimeoutId.current) {
      footerTimeoutId.current = setTimeout(() => setInitiateFotter(true), 400);
    }
  }, [footerRef, initiatedPage]);

  useEffect(() => {
    if (!initiatedPage && !isLoading) {
      logging.logAll('Public Profile version: ', packageJson.version);
      logging.logAll('Shared lib version: ', uilibPackageJson.version);

      setIsLoading(true);

      const baseUrl = getPathfinderApiUrl();
      fetch(`${baseUrl}/public-profile-api/${userId}`)
        .then((res) => {
          if (res.status !== 200) {
            if (res.status === 404) {
              throw new Error('404');
            } else {
              throw new Error('Something went wrong.');
            }
          }

          return res.json();
        })
        .then((data) => {
          setResponseText('');
          setUserIsPublished(true);

          // Do mapping with existing structure
          const mappedData = {
            id: userId,
            userId: data.userId,
            published: true,
            createdAt: data.createdAt,
            basic: {},
            socialMedias: [] as ISocialMedia[],
            sections: [] as any[],
            ...(data.avatar ? { avatar: data.avatar } : {}),
            ...(data.nickname ? { nickname: data.nickname } : {}),
          };

          if (data.personal) {
            Object.assign(mappedData.basic, {
              firstName: data.personal.firstName,
              lastName: data.personal.lastName,
              profilePicture: data.personal.profilePicture,
              dateOfBirth: data.personal?.dateOfBirth,
              currentJobTitle: data.personal?.currentJobTitle,
            });
          }

          if (data.aboutMe) {
            Object.assign(mappedData.basic, {
              aboutMe: data.aboutMe,
            });
          }

          if (data.activity) {
            // @ts-ignore
            mappedData.sections.push({
              type: ComponentTypes.Activity,
              visible: true,
              stars: data.activity.stars,
              lessonsRead: data.activity.lessonsRead,
              exercisePoints: data.activity.exercisePoints,
              quizPoints: data.activity.quizPoints,
              totalPoints: data.activity.totalPoints,
              challenges: data.activity.completedChallenges,
            } as IActivity);
          }

          if (data.interests) {
            // @ts-ignore
            mappedData.sections.push({
              type: ComponentTypes.Interests,
              visible: true,
              tags: data.interests,
            } as IInterests);
          }

          if (data.certifications) {
            // @ts-ignore
            mappedData.sections.push({
              type: ComponentTypes.Certificates,
              visible: true,
              certificates: data.certifications,
            } as ICertificates);
          }

          if (data.skills) {
            // @ts-ignore
            mappedData.sections.push({
              type: ComponentTypes.Skills,
              visible: true,
              skills: data.skills,
            });
          }

          if (data.spaces) {
            // @ts-ignore
            mappedData.sections.push({
              type: ComponentTypes.Spaces,
              visible: true,
              spaces: data.spaces,
            });
          }

          /* if (data.education) {
            // @ts-ignore
            mappedData.sections.push({
              type: ComponentTypes.Education,
              visible: true,
              education: data.education,
            });
          }

          if (data.experience) {
            // @ts-ignore
            mappedData.sections.push({
              type: ComponentTypes.Experience,
              visible: true,
              experience: data.experience,
            });
          } */

          // Mapping Social Medias
          const socialMediaTypes: { [key: string]: keyof typeof data.contacts } = {
            Email: 'email',
            Phone: 'phone',
            LinkedIn: 'linkedin',
            Twitter: 'twitter',
            GitHub: 'github',
          };
          Object.entries(socialMediaTypes).forEach(([type, field]) => {
            const media = mapSocialMedia(data, type as SocialMediaTypes, field);
            if (media) mappedData.socialMedias.push(media);
          });

          // Process cohort data and fix userRank
          if (data.cohort) {
            const cohortData = { ...data.cohort };

            // Find the current user's rank in the cohort users array
            const currentUser = cohortData.users?.find((user: any) => user.userId === data.userId);
            if (currentUser) {
              cohortData.userRank = currentUser.rank;
            } else {
              // If user not found in cohort, set userRank to 0 as fallback
              cohortData.userRank = 0;
            }

            // Map cohort user properties to match interface
            if (cohortData.users) {
              cohortData.users = cohortData.users.map((user: any) => ({
                ...user,
                id: user.userId, // Map userId to id for backward compatibility
                weeklyXP: user.weeklyXp, // Map weeklyXp to weeklyXP
                booster: user.isBoosted, // Map isBoosted to booster
              }));
            }

            Object.assign(mappedData, { cohort: cohortData });
          }

          // Process competition performance data
          if (data.competitionPerformance) {
            Object.assign(mappedData, { competitionPerformance: data.competitionPerformance });
          }

          // Process subscription level
          if (data.subscriptionLevel) {
            Object.assign(mappedData, { subscriptionLevel: data.subscriptionLevel });
          }

          // Process assets
          if (data.assets) {
            Object.assign(mappedData, { assets: data.assets });
          }

          // Fetch avatar assets from API
          fetch(`${getKaiApiBaseUrl()}/pathfinder/league-api/assets`)
            .then((avatarResponse) => {
              if (avatarResponse.ok) {
                return avatarResponse.json();
              }
              throw new Error('Failed to fetch avatar assets');
            })
            .then((avatarData) => {
              // Process assets to prepend full URL to svg and previewSvg
              const processedAssets: Record<string, any> = {};
              const avatarBaseUrl = `${getPathfinderUrl()}/pathfinder/league/assets`;

              Object.entries(avatarData.assets).forEach(([id, asset]: [string, any]) => {
                processedAssets[id] = {
                  ...asset,
                  svg: `${avatarBaseUrl}${asset.svg}`,
                  previewSvg: `${avatarBaseUrl}${asset.previewSvg}`,
                };
              });

              setAvatarAssets(processedAssets);
              setUnlockRules(avatarData.unlockRules);
              setAvatarAssetsLoaded(true);
            })
            .catch((error) => {
              console.error('Failed to fetch avatar assets:', error);
              setAvatarAssetsLoaded(true); // Set to true even on error to avoid infinite loading
            });

          /* Object.assign(mappedData, {
            competitionPerformance: {
              topThreeFinishes: 12,
              leaguePromotions: 3,
              currentStreak: 2,
              bestRank: 12,
            },
            assets: [
              { assetId: 'glasses' },
              { assetId: 'black_glasses_1e' },
              { assetId: 'bracelets_2e' },
              { assetId: 'backpack_1q' },
              { assetId: 'hoodie_2q' },
              { assetId: 'cool_glasses_1cert' },
              { assetId: 'silver_shoes_3s' },
            ],
            cohort: {
              users: [
                {
                  userId: 'a78a4ac3-71e5-4f11-8714-9cb1b5539ac2',
                  nickname: 'EncryptedWolf09417',
                  avatar: {
                    layers: {
                      outfit: 'shirt',
                      mood: 'mood_meditation',
                    },
                    version: 1,
                    colors: {},
                    base: 'base-lynx',
                  },
                  weeklyXP: 1337,
                  rank: 1,
                  isBoosted: false,
                  tier: 'br',
                },
                {
                  userId: 'a6e9db04-919a-4d39-9631-59f151dbe0bd',
                  nickname: 'CyberCache',
                  weeklyXP: 0,
                  rank: 2,
                  isBoosted: false,
                  tier: 'br',
                },
              ],
              userRank: 1,
              totalUsers: 2,
            },
          }); */

          setProfileData(mappedData as unknown as IUserProfileConfiguration);
        })
        .catch((err) => {
          logging.logDebug('error: ', err);

          if (err.message === '404') {
            setResponseText('User does not have a public profile.');
          } else {
            const myNotif: NotificationType = {
              id: uuidv4(),
              dismissed: false,
              type: 'error',
              header: ERROR_HEADING,
              body: <div>Something went wrong loading the profile</div>,
            };

            setResponseText('An error occured fetching the profile data.');
            addNNotification(myNotif);
          }
        })
        .finally(() => {
          setInitiatedPage(true);
          setIsLoading(false);
        });
    }
  }, [addNNotification, initiatedPage, isLoading, mapSocialMedia, setProfileData, setAvatarAssets, setUnlockRules, setAvatarAssetsLoaded, userId]);

  // hook for setting bottom banner to bottom:0px when fixed position
  useEffect(() => {
    if (footerRef.current) {
      if (useIsFooterSetToFixed) {
        footerRef.current.style.bottom = '0px';
      } else {
        footerRef.current.style.bottom = '';
      }
    }
  }, [footerRef, useIsFooterSetToFixed]);

  const closeModal = () => {
    setShowContactInfoModal(false);
  };

  const clickOpenContactMeModal = () => {
    setShowContactInfoModal(true);
  };

  const clickedAchievementItem = (type: ComponentTypes) => {
    logging.logDebug('PublicProfile -> app -> clickedAchievementItem -> type', type);
    let elementId: string | undefined;

    switch (type) {
      case ComponentTypes.Certificates:
        elementId = 'certificates';
        break;
      case ComponentTypes.Activity:
        elementId = 'activity';
        break;
      case ComponentTypes.Discord:
        elementId = 'discord';
        break;
      default:
        elementId = undefined;
    }

    if (elementId) {
      document.getElementById(elementId)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  };

  return (
    <>
      {isLoading ? (
        <LoadingBox text='Loading...' />
      ) : (
        <div className='PublicProfile-body'>
          {userIsPublished ? (
            <>
              {/* Header/Profile Section */}
              <div className='section-box'>
                <User openContactMeModal={clickOpenContactMeModal} clickedAchievementItem={clickedAchievementItem} cohort={profileData.cohort} />
              </div>

              {profileData.sections.map((item: IComponentDefault) => {
                if (item.visible) {
                  switch (item.type) {
                    case ComponentTypes.Certificates: {
                      const certificatesSection = profileData.sections.find((s: any) => s.type === ComponentTypes.Certificates) as any;

                      if (certificatesSection?.certificates?.length > 0) {
                        return (
                          <div key='component-certificates' className='section-box'>
                            <Certificates id='certificates' />
                          </div>
                        );
                      }

                      return null;
                    }

                    case ComponentTypes.Discord:
                      return (
                        <div key='component-discord' className='section-box'>
                          <Discord id='discord' />
                        </div>
                      );
                    case ComponentTypes.Activity:
                      return (
                        <React.Fragment key='activity-and-competition'>
                          {/* Activity Score and Competition Performance in same box */}
                          <div className='section-box'>
                            <Activity id='activity' />
                            {profileData.competitionPerformance && profileData.cohort && (
                              <>
                                <div style={{ margin: '20px 0' }}>
                                  <hr style={{ border: 'none', borderTop: '1px solid #FAFAFA', margin: 0 }} />
                                </div>
                                <CompetitionPerformance competitionPerformance={profileData.competitionPerformance} />
                              </>
                            )}
                          </div>

                          {/* Achievements in separate box */}
                          {profileData.assets && profileData.assets.length > 0 && (
                            <div className='section-box'>
                              <Achievements assets={profileData.assets} />
                            </div>
                          )}

                          {/* Top Skills in separate box */}
                          {(() => {
                            const skillsSection = profileData.sections.find((s: any) => s.type === ComponentTypes.Skills) as any;
                            return skillsSection?.skills?.length > 0 ? (
                              <div className='section-box'>
                                <Skills id='skills' topSkillsOnly />
                              </div>
                            ) : null;
                          })()}
                        </React.Fragment>
                      );

                    case ComponentTypes.Spaces: {
                      const spacesSection = profileData.sections.find((s: any) => s.type === ComponentTypes.Spaces) as any;

                      if (spacesSection?.spaces?.length === 0) {
                        return null;
                      }

                      return (
                        <div key='component-spaces' className='section-box'>
                          <Spaces id='spaces' />
                        </div>
                      );
                    }

                    case ComponentTypes.Interests:
                      return null; // Skip rendering here, will render at bottom
                    default:
                      return null;
                  }
                } else {
                  return null;
                }
              })}

              {/* Render Cohort section only if cohort data and users exist */}
              {profileData.cohort && Array.isArray(profileData.cohort.users) && profileData.cohort.users.length > 0 && (
                <div className='section-box'>
                  <Cohort cohort={profileData.cohort} id={(profileData as any).nickname ?? ''} />
                </div>
              )}

              {/* Render Interests section only if interests data and tags exist */}
              {(() => {
                const interestsSection = profileData.sections.find((item: any) => item.type === ComponentTypes.Interests && item.visible);
                return interestsSection && Array.isArray((interestsSection as any).tags) && (interestsSection as any).tags.length > 0 ? (
                  <div className='section-box'>
                    <Interests id='interests' />
                  </div>
                ) : null;
              })()}

              <div className='PublicProfile-contact-footer-wrapper'>
                <div>&copy; {new Date().getFullYear()}</div>
                <Link to={`/report-abuse/${userId}`} className='PublicProfile-nabuse-btn' target='_blank' rel='noopener noreferrer'>
                  Report Abuse
                </Link>
              </div>
            </>
          ) : (
            initiatedPage && (
              <div className='PublicProfile-no-user'>
                <ShrugIllustration />
                <div>{responseText}</div>
              </div>
            )
          )}
        </div>
      )}

      {initiatedPage && <Banner mRef={footerRef} />}

      {initiatedPage && <ContactModal show={showContactInfoModal} handleClose={closeModal} />}

      <Notifications
        className='PublicProfile-notifications'
        notifications={notifications}
        onClose={(notif: NotificationDismissType) => dismissNotification(notif.id)}
      />
    </>
  );
};

export default PublicProfile;
